import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect, useRef } from "react";

// ---- Cuisine → emoji helper ---------------------------------------------
// Used when a vendor has no logo_url. Match against cuisine_tags / cuisine_type.
const CUISINE_EMOJI_RULES = [
  { test: /(taco|mexican|latin|burrito)/i, emoji: "🌮" },
  { test: /(burger|smash)/i, emoji: "🍔" },
  { test: /(bbq|barbecue|smokehouse|brisket|ribs)/i, emoji: "🍖" },
  { test: /(pizza|italian|pasta)/i, emoji: "🍕" },
  { test: /(coffee|espresso|latte|cafe)/i, emoji: "☕" },
  { test: /(healthy|salad|vegan|vegetarian|bowl)/i, emoji: "🥗" },
  { test: /(noodle|ramen|pho|thai|asian|vietnam|chinese|japanese|sushi)/i, emoji: "🍜" },
  { test: /(dessert|ice ?cream|sweet|donut|bakery|cookie|cupcake)/i, emoji: "🍦" },
];

export function cuisineEmoji(marker = {}) {
  const tags = marker.cuisine_tags || [];
  const candidates = [...tags, marker.cuisine_type || ""].filter(Boolean);
  for (const c of candidates) {
    for (const rule of CUISINE_EMOJI_RULES) {
      if (rule.test.test(c)) return rule.emoji;
    }
  }
  return "🍽️";
}

// ---- Marker icon factory -------------------------------------------------
// Renders a circular badge (vendor logo or cuisine-emoji fallback). When the
// vendor is live we add a green pulsing ring around the badge. When offline
// the ring is hidden and the badge border switches to muted grey.
export const makeIcon = (marker = {}) => {
  const isLive = !!marker.is_live;
  const wrapClass = isLive ? "ff-marker-wrap" : "ff-marker-wrap ff-marker-wrap-offline";
  const ringClass = isLive
    ? "ff-marker-ring ff-marker-ring-live"
    : "ff-marker-ring ff-marker-ring-offline";
  const logo = marker.logo_url || marker.vendor_logo_url || null;
  const inner = logo
    ? `<img src="${logo}" alt="" class="ff-marker-img" onerror="this.style.display='none';this.parentNode.classList.add('ff-marker-fallback');this.parentNode.setAttribute('data-emoji','${cuisineEmoji(marker)}')" />`
    : `<span class="ff-marker-emoji">${cuisineEmoji(marker)}</span>`;

  return L.divIcon({
    className: wrapClass,
    html: `<div class="ff-marker"><div class="${ringClass}"></div><div class="ff-marker-badge">${inner}</div></div>`,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
  });
};

function ClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      onClick?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center)) {
      map.setView(center, zoom ?? map.getZoom(), { animate: true });
    }
  }, [center, zoom, map]);
  return null;
}

export default function MapView({
  center = [40.7128, -74.006],
  zoom = 13,
  markers = [],
  onMapClick,
  onMarkerClick,
  selected,
  selectedId = null,
  height = 380,
  testid = "map",
  interactive = true,
}) {
  const markerRefs = useRef({});

  // Toggle the selected class directly on the marker DOM element so the
  // CSS transition runs smoothly (scale up 1x → 1.25x and back).
  useEffect(() => {
    Object.entries(markerRefs.current).forEach(([id, m]) => {
      if (!m) return;
      const el = m.getElement?.();
      if (!el) return;
      el.classList.toggle(
        "ff-marker-wrap-selected",
        String(id) === String(selectedId)
      );
    });
  });

  const baseProps = interactive
    ? { scrollWheelZoom: true }
    : {
        scrollWheelZoom: false,
        dragging: false,
        doubleClickZoom: false,
        zoomControl: false,
        touchZoom: false,
        boxZoom: false,
        keyboard: false,
        attributionControl: false,
      };

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200" data-testid={testid} style={{ height }}>
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} {...baseProps}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter center={center} zoom={zoom} />
        {onMapClick && interactive && <ClickHandler onClick={onMapClick} />}
        {selected && (
          <Marker position={[selected.lat, selected.lng]} icon={makeIcon({ is_live: true })}>
            <Popup>Selected location</Popup>
          </Marker>
        )}
        {markers.map((m, i) => {
          const id = m.vendor_id || i;
          return (
            <Marker
              key={id}
              position={[m.lat, m.lng]}
              icon={makeIcon(m)}
              ref={(ref) => {
                if (ref) {
                  markerRefs.current[id] = ref;
                } else {
                  delete markerRefs.current[id];
                }
              }}
              eventHandlers={
                onMarkerClick
                  ? { click: () => onMarkerClick(m) }
                  : undefined
              }
            >
              {!onMarkerClick && interactive && (
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold">{m.business_name || "Vendor"}</div>
                    {m.address && <div className="text-slate-500">{m.address}</div>}
                    <div className="mt-1">
                      {m.is_live ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                          <span className="ff-live-dot" /> Live now
                        </span>
                      ) : (
                        <span className="text-slate-500">Offline</span>
                      )}
                    </div>
                  </div>
                </Popup>
              )}
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}
